import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    Sparkles, Send, Mic, Square, Volume2, VolumeX, Plus, Trash2, Loader2,
    MessageSquare, Brain, X, FileDown, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { API_BASE, api, authFetch } from "@/lib/api";
import { ModuleGate } from "@/components/common/ModuleGate";
import { HologramPortrait } from "@/components/fx/HologramPortrait";
import { useSpeechQueue, takeSpeakableChunks } from "@/components/utsav/useSpeechQueue";
import { AttachmentComposer, AttachmentBadges } from "@/components/utsav/AttachmentComposer";
import { MessageArtifacts } from "@/components/utsav/MessageArtifacts";
import { TID } from "@/lib/testIds";

function ChatBubble({ msg, index }) {
    const isUser = msg.role === "user";
    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div
                data-testid={`utsav-message-${index}`}
                className={`max-w-[78%] rounded-[6px] px-4 py-3 ${isUser ? "bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border border-cyan-500/30" : "panel-elevated border border-rose-500/20"}`}
            >
                {!isUser && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <Sparkles size={11} className="text-rose-400" />
                        <span className="text-[10px] font-mono text-rose-300/80 tracking-wide">UTSAV</span>
                        {msg.analysed_files > 0 && (
                            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/25">
                                file analysis · {msg.analysed_files}
                            </span>
                        )}
                    </div>
                )}
                <AttachmentBadges attachments={msg.attachments} />
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line font-body text-decode">{msg.text}</p>
                {msg.image_base64 && (
                    <div className="mt-3">
                        <img
                            src={`data:${msg.mime_type || "image/png"};base64,${msg.image_base64}`}
                            alt="Generated by Utsav"
                            className="rounded-[4px] border border-white/10 max-w-full"
                        />
                        {msg.image_engine && (
                            <div data-testid="utsav-image-engine-label" className="mt-1 text-[9px] font-mono uppercase tracking-wider text-slate-400">
                                {msg.image_engine}{msg.image_edited ? " · edited from your attachment" : ""}
                            </div>
                        )}
                    </div>
                )}
                {!isUser && <MessageArtifacts msg={msg} index={index} />}
            </div>
        </motion.div>
    );
}

const SPEECH_LANGS = [
    { code: "", label: "Auto-detect", short: "AUTO" },
    { code: "en", label: "English", short: "EN" },
    { code: "bn", label: "বাংলা (Bengali)", short: "বাং" },
    { code: "hi", label: "हिन्दी (Hindi)", short: "हि" },
];

const MODELS = [
    { key: "sonnet", label: "Reasoner · Deep", sub: "Deeper reasoning" },
    { key: "haiku", label: "Reasoner · Fast", sub: "Fastest replies" },
];

const IMAGE_ENGINES = [
    { key: "visual_fast", label: "Visual · Fast" },
    { key: "visual_studio", label: "Visual · Studio" },
    { key: "visual_classic", label: "Visual · Classic" },
];

/** Handoff prompts already sent, keyed on `${location.key}:${prompt}` — module
 * scoped so it outlives StrictMode's mount/unmount/mount cycle. */
const consumedHandoffs = new Set();

export default function Utsav() {
    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [model, setModel] = useState("sonnet");
    const [imageEngine, setImageEngine] = useState("visual_fast");
    const [streaming, setStreaming] = useState(false);
    const [streamingText, setStreamingText] = useState("");
    const [toolActive, setToolActive] = useState(null);
    const [autoSpeak, setAutoSpeak] = useState(false);
    const { speaking, push: pushSpeech, stop: stopSpeech } = useSpeechQueue();
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [memory, setMemory] = useState([]);
    const [showMemory, setShowMemory] = useState(false);
    const [newFact, setNewFact] = useState("");
    const [speechLang, setSpeechLang] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [exporting, setExporting] = useState("");

    const abortRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const startedAtRef = useRef(0);
    const scrollRef = useRef(null);

    const loadSessions = useCallback(async () => {
        try {
            const { data } = await api.get("/utsav/sessions");
            setSessions(data);
        } catch (e) { console.warn("Failed to load Utsav sessions", e); }
    }, []);
    const loadMemory = useCallback(async () => {
        try {
            const { data } = await api.get("/utsav/memory");
            setMemory(data);
        } catch (e) { console.warn("Failed to load Utsav memory", e); }
    }, []);
    useEffect(() => { loadSessions(); loadMemory(); }, [loadSessions, loadMemory]);

    // A prompt handed over from the Dashboard Command Center is asked immediately.
    // Guarded by a module-scoped set keyed on the router `location.key` so React's
    // StrictMode double-mount (which resets refs) cannot fire the prompt twice,
    // while a fresh navigation with the same text still works.
    const location = useLocation();
    const handoff = location.state?.prompt;
    const handoffKey = handoff ? `${location.key}:${handoff}` : null;
    useEffect(() => {
        if (!handoffKey || consumedHandoffs.has(handoffKey)) return;
        consumedHandoffs.add(handoffKey);
        window.history.replaceState({}, "");   // survives a page reload too
        sendMessage(handoff);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handoffKey]);
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, streamingText]);

    const stopEverything = () => {
        abortRef.current?.abort();
        stopSpeech();
        setStreaming(false);
        setToolActive(null);
    };

    const selectSession = async (id) => {
        stopEverything();
        try {
            const { data } = await api.get(`/utsav/sessions/${id}`);
            setActiveSessionId(id);
            setModel(data.model || "sonnet");
            setMessages(data.display_messages || []);
        } catch (e) { toast.error("Failed to load conversation"); }
    };

    const newChat = () => { stopEverything(); setActiveSessionId(null); setMessages([]); setAttachments([]); };

    const exportConversation = async (fmt) => {
        if (!activeSessionId) return;
        setExporting(fmt);
        try {
            const res = await api.get(`/utsav/sessions/${activeSessionId}/export`, {
                params: { fmt }, responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `utsav_conversation_${activeSessionId.slice(0, 8)}.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Conversation exported as ${fmt.toUpperCase()}`);
        } catch (e) {
            toast.error("Export failed");
        } finally {
            setExporting("");
        }
    };

    const deleteSession = async (id, e) => {
        e.stopPropagation();
        try {
            await api.delete(`/utsav/sessions/${id}`);
            if (id === activeSessionId) newChat();
            await loadSessions();
        } catch (e2) { toast.error("Failed to delete conversation"); }
    };

    const sendMessage = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || streaming) return;
        const sentAttachments = attachments;
        setInput("");
        setAttachments([]);
        setMessages((m) => [...m, { role: "user", text, attachments: sentAttachments }]);
        setStreaming(true);
        setStreamingText("");
        setToolActive(null);

        const controller = new AbortController();
        abortRef.current = controller;
        let finalImage = null;

        try {
            const res = await authFetch(`${API_BASE}/utsav/chat`, ({
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    session_id: activeSessionId,
                    message: text,
                    model,
                    image_engine: imageEngine,
                    attachment_ids: sentAttachments.map((a) => a.id),
                }),
                signal: controller.signal,
            }));
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            let finalText = "";
            let speechBuffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const blocks = buf.split("\n\n");
                buf = blocks.pop();
                for (const block of blocks) {
                    const evLine = block.split("\n").find((l) => l.startsWith("event: "));
                    const dataLine = block.split("\n").find((l) => l.startsWith("data: "));
                    if (!evLine || !dataLine) continue;
                    const event = evLine.slice(7).trim();
                    const data = JSON.parse(dataLine.slice(6));
                    if (event === "session" && !activeSessionId) setActiveSessionId(data.session_id);
                    else if (event === "tool_start") setToolActive(data.name);
                    else if (event === "delta") {
                        setToolActive(null);
                        setStreamingText((t) => t + data.content);
                        if (autoSpeak) {
                            speechBuffer += data.content;
                            const [chunks, rest] = takeSpeakableChunks(speechBuffer);
                            speechBuffer = rest;
                            chunks.forEach(pushSpeech);
                        }
                    }
                    else if (event === "image") finalImage = data;
                    else if (event === "done") finalText = data.content;
                    else if (event === "error") toast.error(data.detail || "Utsav hit an error");
                }
            }
            setMessages((m) => [...m, {
                role: "assistant", text: finalText,
                image_base64: finalImage?.image_base64, mime_type: finalImage?.mime_type,
                image_engine: finalImage?.engine, image_edited: finalImage?.edited_from_attachment,
                analysed_files: sentAttachments.length,
            }]);
            setStreamingText("");
            setToolActive(null);
            await loadSessions();
            await loadMemory();
            if (autoSpeak && speechBuffer.trim()) pushSpeech(speechBuffer);
        } catch (e) {
            if (e.name !== "AbortError") toast.error("Utsav connection failed");
            setStreamingText("");
        } finally {
            setStreaming(false);
        }
    };

    const toggleRecording = async () => {
        if (recording) {
            mediaRecorderRef.current?.stop();
            setRecording(false);
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            // The browser — not us — decides the container. Chrome gives webm/opus,
            // Safari gives mp4; mislabelling the upload is what broke transcription.
            const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]
                .find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
            const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            chunksRef.current = [];
            startedAtRef.current = Date.now();
            mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const heldFor = Date.now() - (startedAtRef.current || 0);
                const type = mr.mimeType || mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type });
                if (heldFor < 500 || blob.size < 1200) {
                    toast.error("Too short — hold the mic while you speak");
                    return;
                }
                const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
                setTranscribing(true);
                try {
                    const form = new FormData();
                    form.append("audio", blob, `clip.${ext}`);
                    if (speechLang) form.append("language", speechLang);
                    const res = await authFetch(`${API_BASE}/utsav/transcribe`, ({
                        method: "POST",
                        body: form,
                    }));
                    const data = await res.json().catch(() => ({}));
                    if (res.ok && data.text) sendMessage(data.text);
                    else toast.error(data.detail || "Couldn't make out any speech — try again");
                } catch (e) { toast.error("Voice transcription failed — check your connection"); }
                finally { setTranscribing(false); }
            };
            mediaRecorderRef.current = mr;
            mr.start(250);   // timeslice: keeps chunks flowing so short clips aren't empty
            setRecording(true);
        } catch (e) { toast.error("Microphone access denied"); }
    };

    const addFact = async () => {
        const fact = newFact.trim();
        if (!fact) return;
        try {
            await api.post("/utsav/memory", { fact });
            setNewFact("");
            await loadMemory();
        } catch (e) { toast.error("Failed to save fact"); }
    };

    const deleteFact = async (id) => {
        try {
            await api.delete(`/utsav/memory/${id}`);
            await loadMemory();
        } catch (e) { toast.error("Failed to remove fact"); }
    };

    return (
        <ModuleGate module="utsav_agent" label="Utsav AI Agent">
        <div className="grid grid-cols-[240px_1fr] gap-4 h-[calc(100vh-160px)] min-h-[520px]" data-testid="utsav-page">
            {/* Session sidebar — floating glass panel, per user's "detached from edge" cinematic spec */}
            <div className="hud-glass floating-panel p-3 flex flex-col gap-2 overflow-hidden">
                <button data-testid={TID.utsavNewChatBtn} onClick={newChat} className="btn-primary sheen w-full inline-flex items-center justify-center gap-2 text-xs py-2">
                    <Plus size={13} /> New Conversation
                </button>
                <div className="flex-1 overflow-y-auto space-y-1 mt-1">
                    {sessions.map((s) => (
                        <div key={s.id} data-testid={TID.utsavSessionItem(s.id)}
                            onClick={() => selectSession(s.id)}
                            className={`group flex items-center gap-2 px-2.5 py-2 rounded-[4px] cursor-pointer text-xs transition-colors ${s.id === activeSessionId ? "bg-rose-500/10 border border-rose-500/30 text-white" : "hover:bg-white/5 text-slate-400"}`}>
                            <MessageSquare size={12} className="shrink-0" />
                            <span className="truncate flex-1">{s.title}</span>
                            <button data-testid={TID.utsavDeleteSessionBtn(s.id)} onClick={(e) => deleteSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 shrink-0">
                                <Trash2 size={11} />
                            </button>
                        </div>
                    ))}
                    {sessions.length === 0 && <p className="text-[11px] text-slate-500 font-mono text-center py-4">No conversations yet.</p>}
                </div>
            </div>

            {/* Chat panel */}
            <div className="hud-glass floating-panel flex flex-col overflow-hidden">
                <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <HologramPortrait
                            testId={TID.utsavHeaderHologram}
                            className="w-11 h-11 shrink-0"
                            showLabel={false}
                        />
                        <div>
                            <h1 className="font-display text-lg text-white">Utsav</h1>
                            <p className="text-[11px] text-slate-500">Your SAP &amp; technical AI Agent — talks, listens, and acts on this workspace</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {activeSessionId && messages.length > 0 && (
                            <>
                                <button
                                    data-testid={TID.utsavExportBtn("pdf")}
                                    onClick={() => exportConversation("pdf")}
                                    disabled={exporting === "pdf"}
                                    title="Download this conversation as a PDF transcript"
                                    className="p-2 rounded-sm border border-white/10 text-slate-500 hover:text-cyan-300 hover:border-cyan-500/40 transition-colors disabled:opacity-50"
                                >
                                    {exporting === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                                </button>
                                <button
                                    data-testid={TID.utsavExportBtn("md")}
                                    onClick={() => exportConversation("md")}
                                    disabled={exporting === "md"}
                                    title="Download this conversation as Markdown"
                                    className="p-2 rounded-sm border border-white/10 text-slate-500 hover:text-cyan-300 hover:border-cyan-500/40 transition-colors disabled:opacity-50"
                                >
                                    {exporting === "md" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                                </button>
                            </>
                        )}
                        <select data-testid={TID.utsavModelSelect} value={model} onChange={(e) => setModel(e.target.value)} title="Reasoning depth" className="field text-xs py-1.5" style={{ width: "auto", paddingLeft: 10, paddingRight: 6 }}>
                            {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                        </select>
                        <select
                            data-testid={TID.utsavImageEngineSelect}
                            value={imageEngine}
                            onChange={(e) => setImageEngine(e.target.value)}
                            title="Visual engine used when Utsav creates or edits an image. Fast and Studio can also edit an image you attach."
                            className="field text-xs py-1.5"
                            style={{ width: "auto", paddingLeft: 10, paddingRight: 6 }}
                        >
                            {IMAGE_ENGINES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                        </select>
                        <select
                            data-testid={TID.utsavSpeechLangSelect}
                            value={speechLang}
                            onChange={(e) => setSpeechLang(e.target.value)}
                            title="Language you speak in — set it for the most accurate transcription"
                            className="field text-xs py-1.5"
                            style={{ width: "auto", paddingLeft: 10, paddingRight: 6 }}
                        >
                            {SPEECH_LANGS.map((l) => <option key={l.code || "auto"} value={l.code}>{l.label}</option>)}
                        </select>
                        <button
                            data-testid={TID.utsavMemoryToggle}
                            onClick={() => setShowMemory((v) => !v)}
                            title="What Utsav remembers about you"
                            className={`p-2 rounded-sm border transition-colors relative ${showMemory ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-500 hover:text-slate-300"}`}
                        >
                            <Brain size={14} />
                            {memory.length > 0 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-rose-500 text-[8px] flex items-center justify-center text-white font-mono">{memory.length}</span>}
                        </button>
                        <button
                            data-testid={TID.utsavAutoSpeakToggle}
                            onClick={() => { setAutoSpeak((v) => !v); if (speaking) stopSpeech(); }}
                            title={autoSpeak ? "Auto-speak on — click to mute" : "Auto-speak off — click to enable voice replies"}
                            className={`p-2 rounded-sm border transition-colors ${autoSpeak ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-500 hover:text-slate-300"}`}
                        >
                            {autoSpeak ? <Volume2 size={14} /> : <VolumeX size={14} />}
                        </button>
                    </div>
                </div>

                {showMemory && (
                    <div data-testid={TID.utsavMemoryPanel} className="px-4 py-3 border-b border-white/10 bg-cyan-500/[0.03]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-mono text-cyan-300/80 tracking-wide inline-flex items-center gap-1.5"><Brain size={11} /> WHAT UTSAV REMEMBERS ABOUT YOU</span>
                            <button onClick={() => setShowMemory(false)} className="text-slate-500 hover:text-slate-300"><X size={13} /></button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {memory.length === 0 && <p className="text-[11px] text-slate-500 font-mono">Nothing pinned yet — tell Utsav something to remember, or add one manually.</p>}
                            {memory.map((f) => (
                                <span key={f.id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm bg-white/5 border border-white/10 text-slate-300">
                                    {f.fact}
                                    <button data-testid={TID.utsavForgetFactBtn(f.id)} onClick={() => deleteFact(f.id)} className="text-slate-500 hover:text-rose-400"><X size={10} /></button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                data-testid={TID.utsavAddFactInput}
                                value={newFact}
                                onChange={(e) => setNewFact(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addFact()}
                                placeholder="Add a fact manually…"
                                className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-sm px-2.5 py-1.5 text-[11px] text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                            />
                            <button data-testid={TID.utsavAddFactBtn} onClick={addFact} className="btn-ghost text-[11px] px-2.5">Pin</button>
                        </div>
                    </div>
                )}

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
                    {messages.length === 0 && !streamingText && (
                        <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-slate-500">
                            <HologramPortrait
                                testId={TID.utsavEmptyStateHologram}
                                className="w-[150px] h-[150px] mb-4"
                                label="MEET UTSAV · AI AGENT"
                            />
                            <p className="text-sm max-w-sm mt-4">Ask Utsav to look up a T-code, run an ad-hoc SoD simulation, describe a role, draft a BPD/PDD, generate an image, or just talk SAP shop.</p>
                        </div>
                    )}
                    {messages.map((m, i) => <ChatBubble key={i} msg={m} index={i} />)}
                    {(streaming) && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                            <div className="max-w-[78%] rounded-[6px] px-4 py-3 panel-elevated border border-rose-500/20" data-testid={TID.utsavStreamingBubble}>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <Sparkles size={11} className="text-rose-400" />
                                    <span className="text-[10px] font-mono text-rose-300/80 tracking-wide">UTSAV</span>
                                </div>
                                {toolActive ? (
                                    <p className="text-xs text-cyan-300/80 font-mono inline-flex items-center gap-2"><Loader2 size={11} className="animate-spin" /> Running {toolActive}…</p>
                                ) : (
                                    <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line">{streamingText}<span className="inline-block w-1.5 h-3.5 bg-cyan-400 ml-0.5 animate-pulse" /></p>
                                )}
                            </div>
                        </motion.div>
                    )}
                </div>

                <div className="p-4 border-t border-white/10">
                    {(streaming || speaking) && (
                        <div className="mb-2 flex justify-center">
                            <button data-testid={TID.utsavStopBtn} onClick={stopEverything} className="inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-sm bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500/25 transition-colors">
                                <Square size={11} /> Stop
                            </button>
                        </div>
                    )}
                    <AttachmentComposer
                        attachments={attachments}
                        setAttachments={setAttachments}
                        sessionId={activeSessionId}
                        disabled={streaming}
                    >
                        <button
                            data-testid={TID.utsavMicBtn}
                            onClick={toggleRecording}
                            disabled={transcribing}
                            title={recording ? "Stop recording" : "Speak to Utsav"}
                            className={`p-3 rounded-sm border transition-colors shrink-0 disabled:opacity-50 ${recording ? "bg-rose-500/20 border-rose-500/50 text-rose-300 animate-pulse" : "border-white/10 text-slate-400 hover:text-cyan-300"}`}
                        >
                            {transcribing ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
                        </button>
                        <textarea
                            data-testid={TID.utsavChatInput}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                            placeholder={attachments.length ? "Ask Utsav about the attached file(s)…" : "Ask Utsav anything — SAP or otherwise…"}
                            rows={1}
                            className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-sm px-3 py-3 text-sm text-slate-200 focus:border-cyan-500/40 focus:outline-none resize-none"
                        />
                        <button data-testid={TID.utsavSendBtn} onClick={() => sendMessage()} disabled={streaming || !input.trim()} className="btn-primary sheen p-3 disabled:opacity-40 shrink-0">
                            <Send size={16} />
                        </button>
                    </AttachmentComposer>
                </div>
            </div>
        </div>
        </ModuleGate>
    );
}
